import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { showSystemToast } from "../utils/system_toast"

// Suggested-groups section: each summary card hides its per-person review
// rows until "Review" is clicked. Accept/dismiss decisions resolve via turbo
// streams (rows/cards are removed server-side); this controller keeps the
// per-card counts honest, drops emptied cards, hides the section when
// nothing is left, and surfaces a toast per decision.
//
// Submissions are intercepted and sent with fetch so turbo-stream responses
// apply at the page level. Forms inside the network_list frame otherwise
// leave Turbo waiting for frame HTML and the UI appears frozen.
export default class extends Controller {
    static targets = ["card", "cards"]

    connect() {
        this.observer = new MutationObserver(() => this.refresh())
        this.startObserving()
        this.boundSubmit = (event) => this.interceptSubmit(event)
        this.element.addEventListener("submit", this.boundSubmit, true)
    }

    disconnect() {
        this.stopObserving()
        this.element.removeEventListener("submit", this.boundSubmit, true)
    }

    startObserving() {
        if (this.observer && this.hasCardsTarget) this.observer.observe(this.cardsTarget, { childList: true, subtree: true })
    }

    stopObserving() {
        if (this.observer) this.observer.disconnect()
    }

    toggle(event) {
        event.preventDefault()
        let card = event.currentTarget.closest("[data-suggestion-review-target='card']")
        if (card) card.classList.toggle("is-open")
    }

    openAll(event) {
        event.preventDefault()
        this.cardTargets.forEach((card) => card.classList.add("is-open"))
    }

    // Disconnect while we mutate the observed subtree: writing textContent
    // (below) replaces child text nodes, which is itself a childList mutation
    // — left observing, that re-fires refresh() in an infinite loop that
    // freezes the tab. Guarding no-op writes as well keeps churn minimal.
    refresh() {
        this.stopObserving()
        try {
            this.cardTargets.forEach((card) => this.refreshCard(card))
            if (this.cardTargets.length === 0) this.element.remove()
        } finally {
            this.startObserving()
        }
    }

    refreshCard(card) {
        let rows = card.querySelectorAll(".group-suggestion-row").length
        if (rows === 0) {
            card.remove()
            return
        }
        let count = card.querySelector("[data-suggestion-count]")
        if (count && count.textContent !== String(rows)) count.textContent = rows
        let label = card.querySelector("[data-suggestion-label]")
        let labelText = ` ${rows === 1 ? card.dataset.labelOne : card.dataset.labelMany}`
        if (label && label.textContent !== labelText) label.textContent = labelText
    }

    interceptSubmit(event) {
        let form = event.target
        if (form.tagName !== "FORM" || !this.element.contains(form)) return
        if (!this.isSuggestionForm(form)) return

        event.preventDefault()
        event.stopPropagation()
        this.submitForm(form)
    }

    isSuggestionForm(form) {
        let action = form.getAttribute("action") || ""
        return action.includes("/group_suggestions/") || action.includes("/suggestions/dismiss")
    }

    submitForm(form) {
        if (form.dataset.submitting === "true") return
        form.dataset.submitting = "true"
        let submit = form.querySelector("[type='submit']")
        if (submit) submit.disabled = true

        fetch(form.action, {
            method: form.method || "POST",
            body: new FormData(form),
            headers: {
                Accept: "text/vnd.turbo-stream.html",
                "X-CSRF-Token": csrfToken()
            },
            credentials: "same-origin"
        })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`)
                return response.text()
            })
            .then((html) => {
                if (html.trim() && window.Turbo?.renderStreamMessage) window.Turbo.renderStreamMessage(html)
                let toast = form.dataset.toast
                if (toast) showSystemToast(toast)
                if ((form.getAttribute("action") || "").includes("/group_suggestions/")) this.refreshNetworkList()
            })
            .catch(() => showSystemToast("Something went wrong - try again.", { level: "danger" }))
            .finally(() => {
                delete form.dataset.submitting
                if (submit) submit.disabled = false
            })
    }

    refreshNetworkList() {
        let frame = document.querySelector("turbo-frame#network_list")
        if (!frame) return
        if (frame.src) frame.reload()
    }
}
